/**
 * Custom Site Specific Javascript
 *
 * PMC Data Litigation - Premium Mortgage Rehmsmeyer
 *
 * Business Rules:
 * - Option 1 (Sections III, IV, V) and Option 2 (Section VI) are mutually exclusive.
 *   If a claimant selects the Alternative Cash Payment (Section VI), they cannot also
 *   claim OOP Losses, Extraordinary Losses, or Lost Time (and vice versa).
 * - Transaction tables for OOP Losses and Extraordinary Losses are shown inline within
 *   their respective sections, only when the claimant selects the corresponding benefit.
 * - Lost Time child fields (hours radio, description) are shown only when selected.
 * - Credit Monitoring (Section II) is available regardless of which option is chosen.
 * - Ordinary Out-of-Pocket Losses are capped at $325 total across all rows.
 */

import { modal } from "commonAssets/lib/modal";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";

// ---------------------------------------------------------------------------
// Transaction Amount Caps
// ---------------------------------------------------------------------------
const transactionCaps = Object.freeze([
    {
        tableId: "1",
        amountField: "ordinary_loss_amount",
        label: "Ordinary Out-of-Pocket Losses",
        cap: 325,
        warningId: "oop-cap-warning",
    },
    {
        tableId: "2",
        amountField: "extraordinary_loss_amount",
        label: "Extraordinary Losses",
        cap: 5000,
        warningId: "extraordinary-cap-warning",
    },
]);

/** Sum all amount inputs for a given cap config inside its transaction table. */
const getTransactionTotal = ({ tableId, amountField }) => {
    const table = document.querySelector(`#transactions_${tableId}`);
    if (!table) return 0;
    let total = 0;
    table
        .querySelectorAll(`input[name="${amountField}"]`)
        .forEach((input) => {
            total += parseFloat(input.value) || 0;
        });
    return total;
};

/** Show or hide an inline warning banner when a transaction total exceeds its cap. */
const validateCapInline = (capConfig) => {
    const { tableId, label, cap, warningId } = capConfig;
    const table = document.querySelector(`#transactions_${tableId}`);
    if (!table) return;

    const total = getTransactionTotal(capConfig);
    let warning = document.getElementById(warningId);

    if (total > cap) {
        if (!warning) {
            warning = document.createElement("div");
            warning.id = warningId;
            warning.className = "alert alert-danger mt-2";
            warning.setAttribute("role", "alert");
            table.insertAdjacentElement("afterend", warning);
        }
        warning.innerHTML =
            `<strong>${label} total exceeds the $${cap.toLocaleString()} cap.</strong> ` +
            `Your current total is $${total.toFixed(2)}. Please reduce your amounts.`;
    } else if (warning) {
        warning.remove();
    }
};

// ---------------------------------------------------------------------------
// Transaction Table Placement
// ---------------------------------------------------------------------------
// Maps CSS classes on form paragraph wrappers to their transaction table captions.
// The framework renders all transaction tables inside a single #transactions container.
// We relocate each table into its matching claim_fields section on init.
const transactionPlacements = Object.freeze({
    "data-trx-oop-losses": "Ordinary Out-of-Pocket Losses",
    "data-trx-extraordinary-losses": "Extraordinary Losses",
});

const cleanCaption = (caption = "") =>
    caption.replace(/\s*—\s*$/, "").replace(/\s+$/, "").trim();

const byCaption = (collection = []) =>
    collection.reduce((acc, item) => {
        const caption = cleanCaption(item.caption?.innerText || "");
        if (caption) acc[caption] = item;
        return acc;
    }, {});

const mapTablesByCaption = () => {
    const container = document.querySelector("#transactions");
    return container ? byCaption([...container.querySelectorAll("table")]) : {};
};

const removeTransactionStubs = () => {
    const container = document.querySelector("#transactions");
    if (container) container.remove();
    document.querySelectorAll(".data-trx-rmel").forEach((node) => node.remove());
};

const collectTargets = () =>
    Object.keys(transactionPlacements).reduce((acc, className) => {
        const node = document.querySelector(`.${className}`);
        if (node) acc[className] = node;
        return acc;
    }, {});

const placeTransactions = () => {
    const tables = mapTablesByCaption();
    const targets = collectTargets();
    console.log({ tables, targets });
    const ready = Object.entries(transactionPlacements).every(
        ([className, caption]) => targets[className] && tables[caption]
    );
    if (!ready) return false;

    Object.entries(transactionPlacements).forEach(([className, caption]) => {
        const target = targets[className];
        const table = tables[caption];
        if (target.dataset.trxAttached === table.id) return;
        target.dataset.trxAttached = table.id;
        target.append(table);

        // Move the corresponding button controls after the table
        const tableId = table.id.replace("transactions_", "");
        const buttonContainer =
            document.querySelector(`#transaction-controls-${tableId}`) ||
            document.querySelector(`.text-center:has(#add_transactions_${tableId})`);

        if (buttonContainer) {
            buttonContainer.id = `transaction-controls-${tableId}`;
            buttonContainer.dataset.trxControlsAttached = table.id;
            table.insertAdjacentElement("afterend", buttonContainer);
        }
    });

    removeTransactionStubs();
    return true;
};

const initTransactionPlacements = (attempt = 0) => {
    if (placeTransactions() || attempt > 10) return;
    window.setTimeout(() => initTransactionPlacements(attempt + 1), 150);
};

// ---------------------------------------------------------------------------
// Transaction Table Visibility
// ---------------------------------------------------------------------------
// Maps checkbox field names to their corresponding transaction table IDs.
// Table IDs are assigned by the framework in order: first table = 1, second = 2.
const transactionControlMap = Object.freeze({
    oop_losses: "1",
    extraordinary_losses: "2",
});

const getTransactionElements = (tableId) => ({
    table: document.querySelector(`#transactions_${tableId}`),
    controls: document.querySelector(`#transaction-controls-${tableId}`),
});

const setTransactionFieldsRequired = (tableId, isRequired) => {
    const table = document.querySelector(`#transactions_${tableId}`);
    console.log({ table, isRequired });
    if (!table) return;
    table.querySelectorAll("input, textarea, select").forEach((input) => {
        if (isRequired) {
            input.setAttribute("required", "required");
            input.setAttribute("aria-required", "true");
        } else {
            input.removeAttribute("required");
            input.setAttribute("aria-required", "false");
        }
    });
};

const clearTransactionFields = (tableId) => {
    const table = document.querySelector(`#transactions_${tableId}`);
    if (!table) return;
    table.querySelectorAll("input, textarea, select").forEach((input) => {
        if (input.type === "checkbox" || input.type === "radio") {
            input.checked = false;
        } else {
            input.value = "";
        }
    });
};

const hideTransaction = (tableId) => {
    const { table, controls } = getTransactionElements(tableId);
    if (table) table.classList.add("d-none");
    if (controls) {
        controls.classList.add("d-none");
        controls.querySelectorAll("button, a").forEach((btn) => {
            btn.disabled = true;
            btn.setAttribute("aria-disabled", "true");
        });
    }
    setTransactionFieldsRequired(tableId, false);
    clearTransactionFields(tableId);
};

const showTransaction = (tableId) => {
    const { table, controls } = getTransactionElements(tableId);
    if (table) table.classList.remove("d-none");
    if (controls) {
        controls.classList.remove("d-none");
        controls.querySelectorAll("button, a").forEach((btn) => {
            btn.disabled = false;
            btn.setAttribute("aria-disabled", "false");
        });
    }
    setTransactionFieldsRequired(tableId, true);
};

const toggleTransaction = (tableId, isChecked) => {
    (isChecked ? showTransaction : hideTransaction)(tableId);
};

// ---------------------------------------------------------------------------
// Child Field Visibility
// ---------------------------------------------------------------------------
const toggleChildFields = (className, isVisible) => {
    document.querySelectorAll(`.${className}`).forEach((el) => {
        el.style.display = isVisible ? "" : "none";
        el.querySelectorAll("input, textarea, select").forEach((input) => {
            if (isVisible) {
                if (input.dataset.wasRequired === "true") {
                    input.setAttribute("required", "required");
                    input.setAttribute("aria-required", "true");
                }
            } else {
                if (input.hasAttribute("required")) {
                    input.dataset.wasRequired = "true";
                }
                input.removeAttribute("required");
                input.setAttribute("aria-required", "false");
            }
        });
    });
};

// ---------------------------------------------------------------------------
// Section Toggle Functions
// ---------------------------------------------------------------------------

/** Toggle OOP Losses section children and transaction table */
const toggleOopLossesFields = (isChecked) => {
    toggleChildFields("oop-losses-child", isChecked);
    toggleTransaction(transactionControlMap["oop_losses"], isChecked);
};

/** Toggle Extraordinary Losses section children and transaction table */
const toggleExtraordinaryLossesFields = (isChecked) => {
    toggleChildFields("extraordinary-losses-child", isChecked);
    toggleTransaction(transactionControlMap["extraordinary_losses"], isChecked);
};

/** Toggle Lost Time section children (hours radio + description textarea) */
const toggleLostTimeFields = (isChecked) => {
    toggleChildFields("lost-time-child", isChecked);
    if (!isChecked) {
        // Clear lost time fields when unchecked
        document.querySelectorAll('[name="lost_time_hours"]').forEach((r) => {
            r.checked = false;
        });
        const descField = document.querySelector('[name="lost_time_description"]');
        if (descField) descField.value = "";
    }
};

// ---------------------------------------------------------------------------
// Mutual Exclusivity: Option 1 vs Option 2
// ---------------------------------------------------------------------------
const enforceOptionExclusivity = (selectedOption) => {
    const oopCheckbox = document.querySelector('[name="oop_losses"]');
    const extraordinaryCheckbox = document.querySelector('[name="extraordinary_losses"]');
    const lostTimeCheckbox = document.querySelector('[name="lost_time"]');
    const altCashCheckbox = document.querySelector('[name="alternative_cash_payment"]');

    if (selectedOption === "option1") {
        // Uncheck Alternative Cash Payment
        if (altCashCheckbox && altCashCheckbox.checked) {
            altCashCheckbox.checked = false;
            altCashCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
        }
    } else if (selectedOption === "option2") {
        // Uncheck all Option 1 checkboxes and hide their children
        [oopCheckbox, extraordinaryCheckbox, lostTimeCheckbox].forEach((cb) => {
            if (cb && cb.checked) {
                cb.checked = false;
                cb.dispatchEvent(new Event("change", { bubbles: true }));
            }
        });
        toggleOopLossesFields(false);
        toggleExtraordinaryLossesFields(false);
        toggleLostTimeFields(false);
    }
};

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    console.log("[Initiate claim]...");

    // Place transaction tables into their respective sections
    initTransactionPlacements();

    // Hide all transactions by default (before placement completes)
    Object.values(transactionControlMap).forEach(hideTransaction);

    const oopCheckbox = document.querySelector('[name="oop_losses"]');
    const extraordinaryCheckbox = document.querySelector('[name="extraordinary_losses"]');
    const lostTimeCheckbox = document.querySelector('[name="lost_time"]');
    const altCashCheckbox = document.querySelector('[name="alternative_cash_payment"]');

    // Event listeners for Option 1 checkboxes
    if (oopCheckbox) {
        oopCheckbox.addEventListener("change", () => {
            if (oopCheckbox.checked) enforceOptionExclusivity("option1");
            toggleOopLossesFields(oopCheckbox.checked);
        });
    }

    if (extraordinaryCheckbox) {
        extraordinaryCheckbox.addEventListener("change", () => {
            if (extraordinaryCheckbox.checked) enforceOptionExclusivity("option1");
            toggleExtraordinaryLossesFields(extraordinaryCheckbox.checked);
        });
    }

    if (lostTimeCheckbox) {
        lostTimeCheckbox.addEventListener("change", () => {
            if (lostTimeCheckbox.checked) enforceOptionExclusivity("option1");
            toggleLostTimeFields(lostTimeCheckbox.checked);
        });
    }

    // Event listener for Option 2 checkbox
    if (altCashCheckbox) {
        altCashCheckbox.addEventListener("change", () => {
            if (altCashCheckbox.checked) enforceOptionExclusivity("option2");
        });
    }

    // Initialize: hide all child fields and transaction tables on load
    toggleOopLossesFields(false);
    toggleExtraordinaryLossesFields(false);
    toggleLostTimeFields(false);

    // Real-time cap validation for all capped transaction tables
    transactionCaps.forEach((capConfig) => {
        const attachCapListener = (attempt = 0) => {
            const table = document.querySelector(
                `#transactions_${capConfig.tableId}`
            );
            if (table) {
                table.addEventListener("input", (e) => {
                    if (e.target.name === capConfig.amountField) {
                        validateCapInline(capConfig);
                    }
                });
            } else if (attempt < 15) {
                window.setTimeout(() => attachCapListener(attempt + 1), 200);
            }
        };
        attachCapListener();
    });

    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    console.log("[Load class member]...");

    // Re-run visibility toggles after member data is loaded
    const oopCheckbox = document.querySelector('[name="oop_losses"]');
    const extraordinaryCheckbox = document.querySelector('[name="extraordinary_losses"]');
    const lostTimeCheckbox = document.querySelector('[name="lost_time"]');

    if (oopCheckbox) toggleOopLossesFields(oopCheckbox.checked);
    if (extraordinaryCheckbox) toggleExtraordinaryLossesFields(extraordinaryCheckbox.checked);
    if (lostTimeCheckbox) toggleLostTimeFields(lostTimeCheckbox.checked);

    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    console.log("[Process claim]...", claim);

    // Validate all capped transaction tables
    const errors = [];

    transactionCaps.forEach(({ tableId, amountField, label, cap }) => {
        const table = document.querySelector(`#transactions_${tableId}`);
        if (!table || table.classList.contains("d-none")) return;

        const total = getTransactionTotal({ tableId, amountField });
        if (total > cap) {
            claim.flags.valid = false;

            // Mark each amount input with an inline error
            table
                .querySelectorAll(`input[name="${amountField}"]`)
                .forEach((input) => {
                    toggleFieldError(
                        input,
                        true,
                        `Total cannot exceed $${cap.toLocaleString()}`
                    );
                });

            errors.push(
                `<strong>${label}</strong> cannot exceed ` +
                    `<strong>$${cap.toLocaleString()}</strong>. ` +
                    `Your current total is <strong>$${total.toFixed(2)}</strong>.`
            );
        }
    });

    if (errors.length > 0) {
        modal(
            "Amount Exceeds Limit",
            errors.join("<br><br>") +
                `<br><br>Please adjust your amounts before submitting.`
        );
    }

    return claim;
}
